import React from 'react'
import type { NextPage, GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { Box, Divider, useColorModeValue } from '@chakra-ui/react'
import { InfoFrame } from '@/components/InfoFrame'
import { PageHero } from '@/components/PageHero'
import { Highlights } from '@/components/Highlights'
import { RocketIcon } from '@/components/icons/RocketIcon'
import { OptionsIcon } from '@/components/icons/OptionsIcon'
import { KeyIcon } from '@/components/icons/KeyIcon'
import { ClickIcon } from '@/components/icons/ClickIcon'
import { ShopNowCard } from '@/components/ShopNowCard'
import { VerticalSlideshow } from '@/components/VerticalSlideshow'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { SectionWithCards } from '@/components/SectionWithCards'
import { CALENDLY_LINK } from '@/constants'
import { TalktoSales } from '@/features/home/TalktoSales'
import { HighlightItem } from '@/types/highlights.interface'
import { ISlide } from '@/types/strapi/commonComponents.interface'
import { ITalktoSales } from '@/types/strapi/homePage.interface'
import type { CardData } from '@/components/SectionWithCards'
import SeoHead from '@/components/seo-head'

const HostingOrdersPage: NextPage = () => {
  const t = useTranslations()
  const bg = useColorModeValue('blackAlpha.100', 'whiteAlpha.100')
  const dividerBg = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')
  const cardBg = useColorModeValue('white', 'black')
  const cardTitleColor = useColorModeValue('black', 'white')
  const cardDescriptionColor = useColorModeValue(
    'blackAlpha.800',
    'whiteAlpha.800'
  )
  const cardBorderColor = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')

  const items: HighlightItem[] = [
    {
      icon: RocketIcon,
      title: t('hosting-orders.easy-entry'),
      description: t('hosting-orders.start-earning'),
    },
    {
      icon: OptionsIcon,
      title: t('hosting-orders.affordable-options'),
      description: t('hosting-orders.miners-variety'),
    },
    {
      icon: KeyIcon,
      title: t('hosting-orders.turnkey-solution'),
      description: t('hosting-orders.deployed-time'),
    },
    {
      icon: ClickIcon,
      title: t('hosting-orders.beginner-friendly'),
      description: t('hosting-orders.no-barriers'),
    },
  ]

  const facilitiesSlideshow: ISlide[] = [
    {
      id: t('hosting-orders.power-rate-access'),
      title: t('hosting-orders.favorable-energy'),
      description: t('hosting-orders.power-rate-access'),
      image: { url: '/img/favorable-energy.jpg' },
      imageMobile: { url: '/img/favorable-energy-mobile.jpg' },
    },
    {
      id: t('hosting-orders.top-tier-partners'),
      title: t('hosting-orders.world-class-infrastracture'),
      description: t('hosting-orders.top-tier-partners'),
      image: { url: '/img/facility-from-above.jpg' },
      imageMobile: { url: '/img/facility-from-above-mobile.jpg' },
    },
    {
      id: t('hosting-orders.proactive-monitoring'),
      title: t('hosting-orders.expert-technicians'),
      description: t('hosting-orders.proactive-monitoring'),
      image: { url: '/img/technician.jpg' },
      imageMobile: { url: '/img/technician-mobile.jpg' },
    },
  ]

  const benefitsSlideshow: ISlide[] = [
    {
      id: t('hosting-orders.hardware-ownership'),
      title: t('hosting-orders.hardware-ownership'),
      description: t('hosting-orders.customers-own'),
      image: { url: '/img/hardware-ownership.jpg' },
      imageMobile: { url: '/img/hardware-ownership-mobile.jpg' },
    },
    {
      id: t('hosting-orders.reselling-capability'),
      title: t('hosting-orders.reselling-capability'),
      description: t('hosting-orders.easily-sell'),
      image: { url: '/img/hosted-slide.gif' },
      imageMobile: { url: '/img/hosted-slide.gif' },
    },
    {
      id: t('hosting-orders.tax-benefits'),
      title: t('hosting-orders.tax-benefits'),
      description: t('hosting-orders.tax-advantage'),
      image: { url: '/img/tax-benefits.jpg' },
      imageMobile: { url: '/img/tax-benefits.jpg' },
    },
  ]

  const supportSlideshow: ISlide[] = [
    {
      id: t('hosting-orders.miner-monitoring'),
      title: t('hosting-orders.miner-monitoring'),
      description: t('hosting-orders.transparent-monitoring'),
      image: { url: '/img/miners-monitoring.gif' },
      imageMobile: { url: '/img/miners-monitoring.gif' },
    },
    {
      id: t('hosting-orders.rapid-turnaround'),
      title: t('hosting-orders.rapid-turnaround'),
      description: t('hosting-orders.instant-reponses'),
      image: { url: '/img/instant-responses.gif' },
      imageMobile: { url: '/img/instant-responses.gif' },
    },
    {
      id: t('hosting-orders.personalized-support'),
      title: t('hosting-orders.personalized-support'),
      description: t('hosting-orders.dedicated-managers'),
      image: { url: '/img/personal-support.jpg' },
      imageMobile: { url: '/img/personal-support.jpg' },
    },
  ]

  const fleetCards: CardData[] = [
    {
      title: t('hardware-list.miner-marketplace'),
      description: t('hosting-orders.hardware-flexibility'),
      iconImage: { url: '/img/miners-for-sale.png' },
      iconImageMobile: { url: '/img/miners-for-sale-mobile.png' },
    },
    {
      title: t('common.labels.miner-upgrades'),
      description: t('hosting-orders.fleet-optimization'),
      iconImage: { url: '/img/miner-upgrade.png' },
    },
    {
      title: t('dashboard.prepaid-credits.title'),
      description: t('hosting-orders.hosting-cost-savings'),
      iconImage: { url: '/img/prepaid-credits.png' },
      iconImageMobile: { url: '/img/prepaid-credits-mobile.png' },
    },
    {
      title: t('dashboard.services.tabs.repairs'),
      description: t('hosting-orders.comprehensive-repair-services'),
      iconImage: { url: '/img/repairs-page.png' },
      iconImageMobile: { url: '/img/repairs-page-mobile.png' },
      objectFit: 'cover',
    },
    {
      title: t('common.labels.payment-plans'),
      description: t('hosting-orders.flexible-payment'),
      iconImage: { url: '/img/payment-plan-asic.png' },
    },
    {
      title: t('dashboard.billing.automatic-payments'),
      description: t('hosting-orders.recurring-payments'),
      iconImage: { url: '/img/autopay-page.png' },
      objectFit: 'none',
    },
  ]

  const talkToSalesData: ITalktoSales[] = [
    {
      subtitle: t('common.labels.need-help'),
      title: t('common.personalized-guidance'),
      description: t('common.starting-out'),
      button: {
        id: 'talk-to-sales-cta',
        text: t('contact-sales.schedule-a-call'),
        url: CALENDLY_LINK,
        newTab: false,
        iconName: null,
      },
    },
  ]

  return (
    <>
      <SeoHead
        title={t('hosting-orders.seo.title')}
        breadcrumbTitle={t('hosting-orders.seo.breadcrumb')}
        description={t('hosting-orders.seo.description')}
      />
      <PageHero
        title={t('hosting-orders.bitcoin-mining-made-simple')}
        subTitle={t('hosting-orders.hosted-bitcoin-mining')}
        description={t('hosting-orders.own-miners')}
        image="img/hosting-orders-hero.jpg"
        mobileImage="img/hosting-orders-hero-mobile.jpg"
        anchor="what-is-hosted-mining"
      />
      <Box id="what-is-hosted-mining">
        <InfoFrame
          title={t('hosting-orders.hosted-mining')}
          description={t.rich('hosting-orders.simplifies-mining', {
            br: () => <br />,
          })}
        />
      </Box>
      <Highlights
        title={t('hosting-orders.host-with-compass')}
        description={t('hosting-orders.compass-makes-simple')}
        items={items}
        columns={4}
      />
      <ShopNowCard />
      <ContainerBase px={{ base: '32px', md: 4 }}>
        <Box py={{ base: '64px', md: '100px' }}>
          <VerticalSlideshow
            title={t('hosting-orders.world-class')}
            subtitle={t('hosting-orders.top-tier-facilities')}
            slideshow={facilitiesSlideshow}
            reverseLayout
            alignStart
          />
        </Box>
        <Divider bg={dividerBg} opacity={0.5} />
        <Box py={{ base: '64px', md: '100px' }}>
          <VerticalSlideshow
            title={t('hosting-orders.ownership-benefits')}
            subtitle={t('hosting-orders.maintain-control')}
            slideshow={benefitsSlideshow}
            alignStart
          />
        </Box>
        <Divider bg={dividerBg} opacity={0.5} />
        <Box py={{ base: '64px', md: '100px' }}>
          <VerticalSlideshow
            title={t('hosting-orders.round-clock-support')}
            subtitle={t('hosting-orders.unparalleled-support')}
            slideshow={supportSlideshow}
            reverseLayout
            alignStart
          />
        </Box>
      </ContainerBase>
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '42px', md: '100px' }}
        >
          <SectionWithCards
            title={t('hosting-orders.management-services')}
            subtitle={t('hosting-orders.suite-services')}
            cards={fleetCards}
            cardImageOverlay={false}
            cardImageBg="gray.890"
            cardContentPy={8}
            cardBg={cardBg}
            cardTitleColor={cardTitleColor}
            cardDescriptionColor={cardDescriptionColor}
            cardBorderColor={cardBorderColor}
            cardImageHeight="252px"
            alignStart
            columns={2}
          />
        </ContainerBase>
      </Box>
      <TalktoSales talkToSales={talkToSalesData || []} />
    </>
  )
}

export async function getStaticProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default HostingOrdersPage
